// Running models on a dataset: each model picked runs the dataset's tasks it has no result for (all of them on a new
// dataset or for a new model; the added ones after the dataset grew), in Claude Code, with the support agents'
// models picked here. The cost shown is from the averages so far.
import { esc, postStatus } from '../core'
import { RUN_MODELS, SUPPORT_MODELS, SUPPORT_ROLES, type SupportRole } from '../../shared/replay'
import { modelName, money } from './fmt'
import { loadPool, type Pool } from './pool'

export interface RunTarget {
  id: string
  name: string
  /** Each task's type and the models with a result on it. */
  tasks: { id: string; taskType: string; models: string[] }[]
  models: string[]
}

interface Form {
  target: RunTarget
  models: string[]
  extra: string[] // typed-in model ids
  effort: string
  parallel: number
  support: Record<SupportRole, string>
  submitting: boolean
  error: string
}

const MODAL = 'rp-rundialog'
const ROLE_LABEL: Record<SupportRole, string> = { grader: 'Grader', simulator: 'Simulated user', environment: 'Environment' }

let form: Form | null = null
let pool: Pool | null = null
let onDone: () => void = () => {}

export function openRunDialog(target: RunTarget, done: () => void): void {
  // GPT 6.1 Sol in every role, as the studies' runs had it (and taskreplay.launch), so a run's cells are graded
  // like the cells already in the dataset
  const sol = SUPPORT_MODELS.find((s) => s.id === 'gpt-6.1-sol')!.id
  form = {
    target, models: [], extra: [], effort: '', parallel: 16,
    support: { grader: sol, simulator: sol, environment: sol }, submitting: false, error: '',
  }
  onDone = done
  paint()
  loadPool().then((p) => {
    pool = p
    if (!form) return
    // the dataset's own models where tasks wait for them; on a new dataset, the models run most so far
    const waiting = target.models.filter((m) => todo(m).length)
    form.models = waiting.length ? waiting : target.models.length ? [] : (p.planning.models.length ? p.planning.models : RUN_MODELS).slice(0, 3)
    paint()
  }).catch(() => { if (form) { form.error = 'Could not load cost estimates.'; paint() } })
}

function close(): void {
  form = null
  document.getElementById(MODAL)?.remove()
}

/** The tasks `model` has no result for. */
const todo = (model: string) => form!.target.tasks.filter((t) => t.models.indexOf(model) < 0)

function choices(): string[] {
  const f = form!
  return [...new Set([...f.target.models, ...(pool?.planning.models ?? []), ...RUN_MODELS, ...f.extra])]
}

/** Agents per task by type, grading and the replay's agents per cell, the judge per new pair on each task. */
function cost(): { agent: number; grading: number; judging: number; cells: number; unknown: string[] } {
  const f = form!
  const p = pool!.planning
  let agent = 0, cells = 0, pairs = 0
  const unknown: string[] = []
  for (const m of f.models) {
    const by = p.agentCost[m]
    if (!by) unknown.push(m)
    for (const t of todo(m)) {
      agent += by ? by[t.taskType] ?? by[''] ?? 0 : 0
      cells++
    }
  }
  for (const t of f.target.tasks) {
    const fresh = f.models.filter((m) => t.models.indexOf(m) < 0).length
    pairs += (fresh * (fresh - 1)) / 2 + fresh * t.models.length
  }
  return { agent, grading: cells * ((p.gradeCost ?? 0) + (p.harnessCost ?? 0)), judging: pairs * (p.judgeCost ?? 0), cells, unknown }
}

function paint(): void {
  if (!form) return
  let el = document.getElementById(MODAL)
  if (!el) {
    el = document.createElement('div')
    el.id = MODAL
    el.className = 'tr-modal-backdrop'
    document.body.appendChild(el)
  }
  const f = form
  const head = '<div class="tr-modal-head"><div><h2>Run models</h2><div class="tr-sb-sub">' + esc(f.target.name) + ' · ' +
    f.target.tasks.length + ' tasks</div></div><button type="button" class="tr-modal-x" data-x>✕</button></div>'
  if (!pool) {
    el.innerHTML = '<div class="tr-modal rp-rd">' + head + (f.error ? '<div class="notice tr-dlg-err">' + esc(f.error) + '</div>' : '<div class="empty">Loading…</div>') + '</div>'
    wire(el)
    return
  }
  const c = cost()
  const models = choices().map((m) => {
    const n = todo(m).length
    return '<label class="rp-rd-m' + (n ? '' : ' done') + '"><input type="checkbox" data-model="' + esc(m) + '"' +
      (f.models.indexOf(m) >= 0 ? ' checked' : '') + (n ? '' : ' disabled') + '>' +
      '<span class="rp-rd-mn">' + esc(modelName(m)) + '</span><span class="rp-rd-mid mono">' + esc(m) + '</span>' +
      '<span class="rp-rd-mt">' + (n ? n + ' to run' : 'nothing to run') + '</span></label>'
  }).join('')
  const sel = (id: string, value: string, opts: [string, string][]) => '<select class="tr-filter" id="' + id + '">' +
    opts.map(([v, l]) => '<option value="' + esc(v) + '"' + (v === value ? ' selected' : '') + '>' + esc(l) + '</option>').join('') + '</select>'
  el.innerHTML = '<div class="tr-modal rp-rd">' + head +
    (f.error ? '<div class="notice tr-dlg-err">' + esc(f.error) + '</div>' : '') +
    '<div class="rp-rd-body">' +
    '<div class="rp-rd-sec">Models <span class="tr-muted">· Claude Code</span></div><div class="rp-rd-models">' + models + '</div>' +
    '<div class="rp-rd-add"><input type="text" class="tr-filter" id="rpRdNew" placeholder="Model id"><button type="button" class="btn" id="rpRdAdd">Add</button></div>' +
    '<div class="rp-rd-grid">' +
    '<label class="tr-fld"><span>Effort</span>' + sel('rpRdEffort', f.effort, [['', 'Model default'], ['low', 'Low'], ['medium', 'Medium'], ['high', 'High']]) + '</label>' +
    '<label class="tr-fld"><span>Parallel cells</span><input type="number" class="tr-filter" id="rpRdPar" min="1" max="32" value="' + f.parallel + '"></label>' +
    SUPPORT_ROLES.map((r) => '<label class="tr-fld"><span>' + ROLE_LABEL[r] + '</span>' +
      sel('rpRdS-' + r, f.support[r], SUPPORT_MODELS.map((s) => [s.id, s.label] as [string, string])) + '</label>').join('') +
    '</div>' +
    '<div class="rp-tiles">' +
    '<div class="rp-tile"><div class="rp-tile-k">Cells</div><div class="rp-tile-v">' + c.cells + '</div></div>' +
    '<div class="rp-tile"><div class="rp-tile-k">Agents</div><div class="rp-tile-v">' + money(c.agent) + '</div>' +
      (c.unknown.length ? '<div class="rp-tile-s">+ ' + esc(c.unknown.map(modelName).join(', ')) + ', no runs yet</div>' : '') + '</div>' +
    '<div class="rp-tile"><div class="rp-tile-k">Grading</div><div class="rp-tile-v">' + money(c.grading) + '</div></div>' +
    '<div class="rp-tile"><div class="rp-tile-k">Judging</div><div class="rp-tile-v">' + money(c.judging) + '</div></div>' +
    '<div class="rp-tile"><div class="rp-tile-k">Total</div><div class="rp-tile-v"><b>' + money(c.agent + c.grading + c.judging) + '</b></div></div>' +
    '</div></div>' +
    '<div class="tr-modal-foot"><div class="tr-sb-acts"><button type="button" class="btn tr-cancel" data-x>Cancel</button>' +
    '<button type="button" class="btn tr-launch" id="rpRdGo"' + (f.submitting || !c.cells ? ' disabled' : '') + '>' +
    (f.submitting ? 'Starting…' : 'Run ' + c.cells + ' cells') + '</button></div></div></div>'
  wire(el)
}

function wire(el: HTMLElement): void {
  Array.prototype.forEach.call(el.querySelectorAll('[data-x]'), (b: HTMLElement) => { b.onclick = close })
  el.onclick = (e) => { if (e.target === el) close() }
  const f = form
  if (!f || !pool) return
  Array.prototype.forEach.call(el.querySelectorAll('.rp-rd-m input'), (cb: HTMLInputElement) => {
    cb.onchange = () => {
      const m = cb.getAttribute('data-model') || ''
      f.models = cb.checked ? [...f.models, m] : f.models.filter((x) => x !== m)
      paint()
    }
  })
  const add = () => {
    const input = el.querySelector('#rpRdNew') as HTMLInputElement
    const m = input.value.trim()
    if (!m) return
    if (f.extra.indexOf(m) < 0) f.extra.push(m)
    if (f.models.indexOf(m) < 0) f.models.push(m)
    paint()
  }
  ;(el.querySelector('#rpRdAdd') as HTMLElement).onclick = add
  ;(el.querySelector('#rpRdNew') as HTMLInputElement).onkeydown = (e) => { if (e.key === 'Enter') add() }
  const effort = el.querySelector('#rpRdEffort') as HTMLSelectElement
  effort.onchange = () => { f.effort = effort.value }
  const par = el.querySelector('#rpRdPar') as HTMLInputElement
  par.onchange = () => { f.parallel = Math.max(1, Math.min(32, Math.round(Number(par.value) || 16))) }
  for (const r of SUPPORT_ROLES) {
    const s = el.querySelector('#rpRdS-' + r) as HTMLSelectElement
    s.onchange = () => { f.support[r] = s.value }
  }
  ;(el.querySelector('#rpRdGo') as HTMLElement).onclick = () => submit()
}

function submit(): void {
  const f = form
  if (!f || f.submitting) return
  f.submitting = true
  f.error = ''
  paint()
  postStatus('/api/replay/datasets/' + encodeURIComponent(f.target.id) + '/runs', {
    models: f.models, effort: f.effort || undefined, maxParallel: f.parallel, support: f.support,
  }).then((r: any) => {
    if (!form) return
    if (r.status >= 200 && r.status < 300) { close(); onDone() }
    else { form.submitting = false; form.error = r.body?.error || 'Starting the run failed.'; paint() }
  }).catch(() => { if (form) { form.submitting = false; form.error = 'Starting the run failed. Is the server reachable?'; paint() } })
}
